// Realtime client — single EventSource connection per logged-in
// session, with reconnect-with-backoff and a tiny pub/sub surface so
// any component can subscribe to event types ('message_created',
// 'notification_created', etc.) without each one managing its own
// EventSource.
//
// Why one connection: browsers cap EventSource connections per origin
// (~6 in Chrome, fewer in some others). One shared connection lets
// every tab/component get every event without contention.
//
// Auth note: EventSource can't set Authorization headers, so the
// backend exposes a `POST /realtime/grant` that swaps the long-lived
// auth JWT for a 60-second stream token. We mint one of those before
// every connect/reconnect — short-lived tokens reduce the leak
// surface in proxy / access logs that record query strings.
//
// Reconnect: EventSource auto-reconnects, but it ignores `retry:` if
// the server closes 401/403/404. We layer our own reconnect on top
// (capped exponential backoff, 1s → 30s) so a deploy/restart, a
// rotated token, or transient network blips all recover.

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5001/api';

let source = null;
let token = null;
let reconnectAttempt = 0;
let reconnectTimer = null;
let intentionallyClosed = false;
const listeners = new Map(); // type -> Set<fn>

function notify(type, data) {
  const set = listeners.get(type);
  if (!set) return;
  for (const fn of set) {
    try { fn(data); } catch (err) { console.warn('[realtime] listener threw', err); }
  }
}

function clearReconnect() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function scheduleReconnect() {
  if (intentionallyClosed) return;
  clearReconnect();
  // Equal-jitter exponential backoff. Without jitter, every connected
  // client schedules its first retry at exactly cap/2 (1s) after a
  // Railway redeploy drops the listener — thundering-herd of grant +
  // stream requests at the same wall-clock instant. Equal jitter
  // (half deterministic + half random in [0, half]) spreads the same
  // pool across [cap/2, cap] without changing the cap. AWS arch-blog
  // canonical pattern; cheap and noticeably better than ±10% noise.
  // Cap stays at 30s — the deploy cycle is ~15s so we want to be
  // re-connected by then, not minutes later.
  const cap = Math.min(30000, 1000 * 2 ** reconnectAttempt);
  const half = cap / 2;
  const delay = half + Math.random() * half;
  reconnectAttempt += 1;
  reconnectTimer = setTimeout(() => {
    if (!token || intentionallyClosed) return;
    open();
  }, delay);
}

async function grantStreamToken() {
  // Hand-rolled fetch (rather than the axios `api` instance) keeps
  // this module zero-dep so it can be imported from places that
  // shouldn't drag in the whole API client (e.g. AuthContext, which
  // is imported very early). Auth is the long-lived JWT in
  // localStorage — same one axios uses.
  const auth = (typeof window !== 'undefined' && window.localStorage?.getItem('token')) || token;
  if (!auth) throw new Error('no auth token');
  const r = await fetch(`${API_BASE}/realtime/grant`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${auth}` },
  });
  if (!r.ok) throw new Error(`grant ${r.status}`);
  const j = await r.json();
  if (!j?.token) throw new Error('grant returned no token');
  return j.token;
}

async function open() {
  if (!token) return;
  // Replace any existing connection (e.g. if open() is called twice
  // because of a fast token rotation).
  if (source) {
    try { source.close(); } catch { /* ignore */ }
    source = null;
  }

  let streamToken;
  try {
    streamToken = await grantStreamToken();
  } catch (err) {
    console.warn('[realtime] grant failed', err.message);
    scheduleReconnect();
    return;
  }

  const url = `${API_BASE}/realtime/stream?token=${encodeURIComponent(streamToken)}`;
  let es;
  try {
    es = new EventSource(url);
  } catch (err) {
    console.warn('[realtime] EventSource construct failed', err);
    scheduleReconnect();
    return;
  }
  source = es;

  // 'ready' fires once the backend has authed us. Reset backoff so
  // the next failure starts from 1s again.
  es.addEventListener('ready', () => {
    reconnectAttempt = 0;
    notify('ready', null);
  });

  // Typed events. Add new ones here as the backend adds them.
  ['message_created', 'notification_created', 'feed_event', 'typing', 'read_receipt'].forEach((type) => {
    es.addEventListener(type, (ev) => {
      let data = null;
      try { data = ev.data ? JSON.parse(ev.data) : null; } catch { /* malformed payload, drop */ }
      notify(type, data);
      // Update the home-screen badge when an in-app notification or
      // message arrives. The SW push handler covers the offline case;
      // this covers the open-tab case so the icon stays in sync. The
      // backend sends `unread` on these payloads when known; if it's
      // missing we fall through (the next event with an `unread` count
      // will reconcile).
      if ((type === 'notification_created' || type === 'message_created') && data && typeof data.unread === 'number') {
        try {
          if (typeof navigator !== 'undefined' && navigator.setAppBadge) {
            if (data.unread > 0) navigator.setAppBadge(data.unread);
            else navigator.clearAppBadge?.();
          }
        } catch { /* unsupported browser — ignore */ }
      }
    });
  });

  es.onerror = () => {
    // EventSource fires onerror on both transient drop and final
    // close. readyState === CLOSED means the browser gave up — we
    // need to recreate.
    if (es.readyState === EventSource.CLOSED) {
      try { es.close(); } catch { /* ignore */ }
      source = null;
      scheduleReconnect();
    }
    // CONNECTING state: browser is already retrying internally,
    // leave it alone.
  };
}

// Pause / resume on tab visibility — hold the SSE connection open
// while the tab is visible, drop it after 60s in the background to
// stop the radio firing keepalives every cycle on Android Chrome
// PWAs (and burning battery for nothing — push notifications cover
// the offline interval anyway). Reconnect immediately on visible.
let visibilityTimer = null;
let visibilityHooked = false;
function ensureVisibilityHook() {
  if (visibilityHooked || typeof document === 'undefined') return;
  visibilityHooked = true;
  document.addEventListener('visibilitychange', () => {
    if (!token) return;
    if (document.hidden) {
      visibilityTimer = setTimeout(() => {
        if (!document.hidden) return;
        if (source) {
          try { source.close(); } catch { /* ignore */ }
          source = null;
        }
        clearReconnect();
      }, 60_000);
    } else {
      if (visibilityTimer) {
        clearTimeout(visibilityTimer);
        visibilityTimer = null;
      }
      if (token && (!source || source.readyState === EventSource.CLOSED)) {
        intentionallyClosed = false;
        reconnectAttempt = 0;
        open();
      }
    }
  });
}

// Connect (or reconnect) the realtime stream for `nextToken`. Safe to
// call repeatedly with the same token — it's a no-op when the
// connection is already live.
export function connect(nextToken) {
  if (!nextToken) {
    disconnect();
    return;
  }
  intentionallyClosed = false;
  ensureVisibilityHook();
  if (token === nextToken && source && source.readyState !== EventSource.CLOSED) {
    return;
  }
  token = nextToken;
  reconnectAttempt = 0;
  open();
}

export function disconnect() {
  intentionallyClosed = true;
  clearReconnect();
  token = null;
  reconnectAttempt = 0;
  if (source) {
    try { source.close(); } catch { /* ignore */ }
    source = null;
  }
  // Subscribers stay registered through a logout / re-login cycle.
  // Each on() returns its own unsubscribe (tied to the caller's
  // useEffect cleanup), so dropping the whole map here orphans every
  // still-mounted component (Header, App-level modals) from future
  // SSE events: their useEffect deps haven't changed so they never
  // re-register, and their stored unsubscribe fn no-ops on a Set
  // that's been cleared. The earlier 'orphan callbacks' worry was
  // about HMR in dev — fine to accept; component-owned lifecycle is
  // the source of truth.
}

// Subscribe to a typed event. Returns an unsubscribe fn.
export function on(type, fn) {
  if (!listeners.has(type)) listeners.set(type, new Set());
  listeners.get(type).add(fn);
  return () => {
    const set = listeners.get(type);
    if (!set) return;
    set.delete(fn);
    if (set.size === 0) listeners.delete(type);
  };
}

// Test/debug helper — fire an event into the bus locally without a
// real server roundtrip. Not used in app code.
export function _emitForTest(type, data) {
  notify(type, data);
}
